import React from "react";
import ResetStyles from "./reset.js";
import { connect } from "react-redux";
import { t } from "../../core/translate";
import { selectors as userSelectors } from "../../../redux/user";
import { actions as modalActions } from "../../../redux/modals";
import { selectors as routerSelectors } from "../../../redux/router";
import { selectors as librarySelectors } from "../../../redux/ui/library";
import { selectors as searchSelectors } from "../../../redux/search";
import { selectors as bridgeSelectors } from "../../../redux/bridge";
import { selectors as booksSelectors } from "../../../redux/books";
import { selectors as brandSelectors } from "../../../redux/brand";
import { selectors as licenseSelectors } from "../../../redux/license";
import Header from "./header";
import Footer from "./footer";
import CandyStripe from "./candystripe";
import styled, { withTheme } from "styled-components";
import ModalManager from "../../components/modals/ModalManager";
import ConnectedPanel from "./panel";
import LibraryTabBar from "./library-tab-bar";
import SearchScreen from "./search/screen";
import BodyScroller from "../../components/batchrenderer/BodyScroller";
import Slideout from "./header/slideout";
import LibraryHelpers from "../../../stores/library-helpers";
import { withAllMatches } from "../../components/mediaquery/breakpoints";
import { actions as libraryActions } from "../../../redux/ui/library";
import CookieCat from "../../../components/accept-cookies/banner";
import DebugTools from "../../components/debug/DebugTools";
import SyncListeners from "./sync-listeners";
import { OffScreenStyles } from "../../components/mixins/index.js";

const ScrollOffset = styled.main.attrs({ role: "main" })`
  left: ${props => `${props.offset}px` || ""};
  padding-top: ${props => (props.headerVisible ? "44px" : "0")};
  padding-bottom: ${props => (props.footerVisible ? "88px" : "0")};
  position: ${props => (props.offset ? "absolute" : "")};
  top: ${props => (props.offset ? 0 : "")};
`;

const MOBILE_MENU_WIDTH = 260;

const NewLibraryContainer = styled.div``;

const SkipNav = styled.a`
  position: fixed;
  top: 12px;
  left: 200px;
  color: ${props => props.theme.vstui.color.white};
  text-decoration: none;
  &:focus {
    z-index: 6;
  }
  &:not(:focus) {
    ${OffScreenStyles}
  }
`;

class Library extends React.Component {
  componentDidMount() {
    this.checkUnavailableISBN({});
    if (this.props.readyToShowToasts) {
      this._checkExpiration();
    }
    this.props.closePanel();
    this.initialWidth = window.innerWidth;
  }

  componentDidUpdate(prevProps) {
    this.checkUnavailableISBN(prevProps);
    if (!prevProps.readyToShowToasts && this.props.readyToShowToasts) {
      this._checkExpiration();
    }
  }

  _checkExpiration() {
    this.expirationToastTimeoutId = setTimeout(() => {
      const { expiredBooks, expiringAssets } = this.props;
      expiredBooks.forEach(book => {
        LibraryHelpers.bookHasExpired(book);
      });
      expiringAssets.forEach(asset => {
        LibraryHelpers.expiringBookToast(asset);
      });
    }, 3000);
  }

  checkUnavailableISBN = prevProps => {
    if (
      this.props.unavailableISBN &&
      this.props.unavailableISBN !== prevProps.unavailableISBN
    ) {
      this.props.openModal("BookUnavailable", {
        isbn: this.props.unavailableISBN
      });
    }
  };

  componentWillUnmount() {
    LibraryHelpers.exitLibraryCloseToast();
    clearTimeout(this.expirationToastTimeoutId);
  }

  _redeem() {
    this.props.openRedeemModal();
  }

  renderLibraryContent(searchScreen, native) {
    if (searchScreen) {
      return <SearchScreen />;
    }

    return <LibraryTabBar tabs={native} />;
  }

  render() {
    const {
      native,
      isMobileMenuOpen,
      bridgeUserFetching,
      arePromotionsFetching,
      location,
      isSmall,
      licenseUpdating,
      asyncLicenseUpdate,
      query
    } = this.props;

    const headerVisible = !native;
    const footerVisible =
      (location.hash === "#/" || location.hash === "") && !native && !isSmall;
    const offset = isMobileMenuOpen ? MOBILE_MENU_WIDTH : 0;
    const searchScreen =
      query &&
      (location.hash === "#/search" || location.hash === "#/books/bookshelf");

    return (
      <NewLibraryContainer>
        <CookieCat />
        <ResetStyles noScroll={isMobileMenuOpen} />
        <SyncListeners />
        <BodyScroller />
        <div>
          {!isSmall && (
            <SkipNav
              href="#"
              onClick={e => {
                e.preventDefault();
                document.getElementById("main").focus();
              }}
            >
              {t("library.skip", "Skip to Main Content")}
            </SkipNav>
          )}
          <Slideout
            width={MOBILE_MENU_WIDTH}
            initialWidth={this.initialWidth}
            isOpen={isMobileMenuOpen}
          />

          {headerVisible && <Header offset={offset} />}
          <ScrollOffset
            id="main"
            headerVisible={headerVisible}
            footerVisible={footerVisible}
            offset={offset}
            tabIndex={-1}
            searchScreen={searchScreen}
          >
            {this.renderLibraryContent(searchScreen, native)}
          </ScrollOffset>

          {(bridgeUserFetching ||
            arePromotionsFetching ||
            (licenseUpdating && asyncLicenseUpdate)) && (
            <CandyStripe offset={footerVisible ? 80 : 0} />
          )}

          {footerVisible && (
            <Footer redeem={() => this._redeem()} offset={offset} />
          )}

          <ModalManager />
          <ConnectedPanel />
          <DebugTools />
        </div>
      </NewLibraryContainer>
    );
  }
}

const mapStateToProps = (state, props) => ({
  user: userSelectors.getUser(state),
  expiringAssets: booksSelectors.getExpiringStorePurchases(state),
  expiredBooks: booksSelectors.getExpiredStoreBooks(state),
  location: routerSelectors.getCurrentRoute(state),
  native: routerSelectors.isNative(state),
  unavailableISBN: routerSelectors.getQueryParam(state, "unavailable"),
  query: searchSelectors.getCurrentQuery(state),
  isMobileMenuOpen: librarySelectors.isMobileMenuOpen(state),
  bridgeUserFetching: bridgeSelectors.isUserFetching(state),
  arePromotionsFetching: bridgeSelectors.arePromotionsFetching(state),
  licenseUpdating: licenseSelectors.isLicenseUpdating(state),
  asyncLicenseUpdate: brandSelectors.getBrandValue(
    state,
    "Library.asyncLicense",
    false
  ),
  books: booksSelectors.getBooksByIsbn(state, booksSelectors.getIsbns(state)),
  readyToShowToasts:
    booksSelectors.finishedLoading(state) &&
    booksSelectors.getExpiredLoadStatus(state) === "done",
  expiredStoreUrl: brandSelectors.storeProductsUrl(state, props.isbn),
  isBridgeSessionReady: bridgeSelectors.isBridgeSessionReady(state)
});

const mapDispatchToProps = dispatch => ({
  openModal: (modal, props) => dispatch(modalActions.openModal(modal, props)),
  openRedeemModal: () => dispatch(modalActions.openRedeemModal()),
  closePanel: () => dispatch(libraryActions.closePanel())
});

export default withTheme(
  connect(mapStateToProps, mapDispatchToProps)(withAllMatches(Library))
);
